import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AllLeadsTab from './components/AllLeadsTab';
import InsightsTab from './components/InsightsTab';
import LeadDetail from './components/LeadDetail';
import MeSheet from './components/MeSheet';
import NotificationCenter from './components/NotificationCenter';
import QueueTab from './components/QueueTab';
import UserManagementPanel from './components/UserManagementPanel';
import { Softphone } from './components/Softphone';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import { useIsDesktop } from './hooks/useMediaQuery';
import { useCallQueueSSE } from './hooks/useCallQueueSSE';
import { API_ENDPOINTS } from './config';
import { useFollowupNotifications } from './hooks/useFollowupNotifications';
import type { Tab } from './types';

const TAB_STORAGE_KEY = 'crm_active_tab';

const getSavedTab = (): Tab => {
  try {
    const saved = localStorage.getItem(TAB_STORAGE_KEY);
    const validTabs: Tab[] = ['unassigned', 'queue', 'leads', 'insights'];
    if (saved && validTabs.includes(saved as Tab)) {
      return saved as Tab;
    }
    if (saved === 'all') return 'leads';
  } catch {
    // ignore
  }
  return 'queue';
};

const getTabs = (user: { role: string } | null) => {
  const isAdminOrManager =
    user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_admin';

  return [
    { id: 'unassigned', label: 'New / Unassigned', icon: '🆕' },
    { id: 'queue', label: 'My Queue', icon: '⚡' },
    { id: 'leads', label: isAdminOrManager ? 'All Leads' : 'My Leads', icon: '☰' },
    { id: 'insights', label: 'Insights', icon: '📊' },
  ] as { id: Tab; label: string; icon: string }[];
};

function AppContent() {
  const [tab, setTab] = useState<Tab>(getSavedTab());
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [meOpen, setMeOpen] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const { user, isLoading, logout } = useAuth();
  const isDesktop = useIsDesktop();
  
  const tabs = getTabs(user);
  const isPrivileged =
    user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_admin';
  
  // Enable follow-up notifications when user is logged in
  useFollowupNotifications(!!user);

  // SSE connection for real-time call queue
  const { waitingCalls, isConnected: sseConnected, error: sseError, lastEvent: sseLastEvent } = useCallQueueSSE(user?.email || '');

  // Keep debug visibility into the IVR digit-4 queue table. Audio is handled by the Vobiz WebRTC SDK.
  useEffect(() => {
    console.log('=== APP SSE STATE ===');
    console.log('SSE Connected:', sseConnected);
    console.log('SSE Error:', sseError);
    console.log('SSE Last Event:', sseLastEvent);
    console.log('Waiting Calls:', waitingCalls.length);
    console.log('User Email:', user?.email);
  }, [waitingCalls, sseConnected, sseError, sseLastEvent, user?.email]);

  const handleHangupCall = async (callUuid: string) => {
    try {
      console.log('APP: Hanging up call:', callUuid);
      
      const response = await fetch(API_ENDPOINTS.CALL_QUEUE.HANGUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_uuid: callUuid,
          agent_id: user?.email,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to hangup call');
      }

      console.log('APP: Call hung up successfully');
      
      // Update agent status back to available
      await fetch(API_ENDPOINTS.AGENT.STATUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user?.email,
          status: 'available',
        }),
      });

      alert('Call ended and converted to lead');
    } catch (error) {
      console.error('Failed to hangup call:', error);
      alert('Failed to hangup call: ' + String(error));
    }
  };

  // Persist tab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      // ignore
    }
  }, [tab]);

  useEffect(() => {
    if (!isLoading && user && !tabs.find((t) => t.id === tab)) {
      setTab('queue');
    }
  }, [isLoading, tab, tabs, user]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">CRM System</h1>
          <p className="text-slate-600 mb-6">Please sign in to access the dashboard</p>
          <div className="space-x-4">
            <a
              href="/login"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  const userRole = user.role;

  const current = tabs.find((t) => t.id === tab);

  // Render the main column (header + tab content + bottom nav)
  const mainCol = (
    <div
      className={`flex flex-col bg-slate-50 ${
        isDesktop ? 'w-[28rem] h-screen flex-none border-r border-slate-200' : 'min-h-screen mx-auto max-w-md shadow-xl'
      }`}
    >
      <header className="sticky top-0 z-10 bg-wechat-header px-4 pb-3 pt-[env(safe-area-inset-top,12px)] text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-300">Edge CRM</div>
            <h1 className="text-lg font-semibold">{current?.label}</h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter onNotificationClick={(leadId) => setSelected(leadId)} />
            {isPrivileged && (
              <button
                onClick={() => setShowUsersPanel(true)}
                className="rounded-full bg-slate-700/60 px-3 py-1 text-xs text-slate-100 active:bg-slate-700"
              >
                Users
              </button>
            )}
            <button
              onClick={() => setMeOpen(true)}
              className="flex items-center gap-2 rounded-full bg-slate-700/60 px-3 py-1 text-xs text-slate-100 active:bg-slate-700"
            >
              <span className="h-2 w-2 rounded-full bg-wechat-accent" />
              <span className="truncate max-w-[120px]">{user.email.split('@')[0] || 'Me'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 ${isDesktop ? 'overflow-y-auto' : 'pb-20'}`}>
        <section hidden={tab !== 'unassigned'}>
          <QueueTab
            active={tab === 'unassigned'}
            onSelect={setSelected}
            onChanged={() => setReloadKey((n) => n + 1)}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            view="unassigned"
            title="New / Unassigned Leads"
            subtitle="Claim a lead before you start working it."
          />
        </section>
        <section hidden={tab !== 'queue'}>
          <QueueTab
            active={tab === 'queue'}
            onSelect={setSelected}
            onChanged={() => setReloadKey((n) => n + 1)}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            view="queue"
            title="My Queue"
            subtitle="Active leads that need attention now."
          />
        </section>
        <section hidden={tab !== 'leads'}>
          <AllLeadsTab
            active={tab === 'leads'}
            onSelect={setSelected}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            role={user.role}
          />
        </section>
        <section hidden={tab !== 'insights'}>
          <InsightsTab active={tab === 'insights'} reloadKey={reloadKey} role={user.role} />
        </section>
      </main>

      <nav
        className={`fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur ${
          isDesktop ? 'left-0 right-auto w-[28rem] max-w-md' : 'mx-auto max-w-md'
        }`}
      >
        <div className="flex justify-center items-center gap-4 px-4">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex flex-col items-center gap-0.5 py-2 px-3 text-[11px] ${
                  active ? 'text-wechat-accent font-semibold' : 'text-slate-500'
                }`}
              >
                <span className="text-lg leading-none">{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {showUsersPanel && isPrivileged && (
        <div className="fixed inset-0 z-50 bg-slate-900/40">
          <div className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Admin</div>
                <h2 className="text-base font-semibold text-slate-900">Users</h2>
              </div>
              <button onClick={() => setShowUsersPanel(false)} className="text-sm text-slate-500">
                Close
              </button>
            </div>
            <UserManagementPanel active={showUsersPanel} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-full bg-wechat-bg">
      {isDesktop ? (
        // Desktop split-pane: list on left, detail on right
        <div className="mx-auto flex h-screen max-w-[90rem] bg-slate-50 shadow-xl overflow-hidden">
          {mainCol}
          <div className="flex flex-1 flex-col overflow-hidden">
            {selected ? (
              <LeadDetail
                leadId={selected}
                me={user.email}
                userRole={userRole}
                onClose={() => setSelected(null)}
                onChanged={() => setReloadKey((n) => n + 1)}
                inline
              />
            ) : (
              <div className="flex flex-1 items-center justify-center bg-slate-50 p-12 text-center overflow-auto">
                <div className="max-w-sm">
                  <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-2xl">
                    ⚡
                  </div>
                  <h2 className="text-base font-semibold text-slate-700">Pick a lead</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Tap any card on the left to see the full timeline, call history, WhatsApp
                    templates, and quick-action buttons.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {mainCol}
          {selected && (
            <LeadDetail
              leadId={selected}
              me={user.email}
              userRole={userRole}
              onClose={() => setSelected(null)}
              onChanged={() => setReloadKey((n) => n + 1)}
            />
          )}
        </>
      )}

      {meOpen && (
        <MeSheet
          onClose={() => setMeOpen(false)}
          onSignOut={async () => {
            await logout();
            setMeOpen(false);
          }}
        />
      )}

      {/* Debug Panel */}
      {showDebugPanel && (
        <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg p-4 w-80 border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-sm">SSE Debug Panel</h3>
            <button onClick={() => setShowDebugPanel(false)} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">SSE Connected:</span>
              <span className={sseConnected ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {sseConnected ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Waiting Calls:</span>
              <span className="font-medium">{waitingCalls.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Event:</span>
              <span className="font-medium">{sseLastEvent ? new Date(sseLastEvent).toLocaleTimeString() : 'N/A'}</span>
            </div>
            {sseError && (
              <div className="flex justify-between">
                <span className="text-gray-600">Error:</span>
                <span className="text-red-600">{sseError}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">User:</span>
              <span className="font-medium">{user?.email || 'N/A'}</span>
            </div>
            <div className="pt-2 border-t">
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.TEST.QUEUE_EVENT, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ customer_phone: '919999999999', campaign: 'test' }),
                    });
                    const data = await response.json();
                    console.log('Test queue event created:', data);
                    alert('Test queue event created: ' + data.queue_id);
                  } catch (error) {
                    console.error('Failed to create test queue event:', error);
                    alert('Failed to create test queue event');
                  }
                }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded text-xs mb-2"
              >
                Test Queue Event
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.CALL_QUEUE.HANGUP, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        call_uuid: 'test-uuid',
                        agent_id: user?.email,
                      }),
                    });
                    const data = await response.json();
                    console.log('Call hung up:', data);
                    alert('Call hung up successfully');
                  } catch (error) {
                    console.error('Failed to hangup call:', error);
                    alert('Failed to hangup call');
                  }
                }}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded text-xs"
              >
                Test Hangup (Manual)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Toggle Button */}
      <button
        onClick={() => setShowDebugPanel(!showDebugPanel)}
        className="fixed bottom-20 right-4 z-40 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700"
        title="Toggle Debug Panel"
      >
        🐛
      </button>

      <Softphone agentId={user.email} />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}
